import React, { useMemo } from "react";
import Field from "./shared/Field";
import HeroImageUploader from "./shared/HeroImageUploader";

function normalizeServiceTemplate(tpl) {
  const t = String(tpl ?? "").toLowerCase().trim();
  if (t === "1" || t === "template1" || t.endsWith("template1")) return "template1";
  if (t === "2" || t === "template2" || t.endsWith("template2")) return "template2";
  if (t === "3" || t === "template3" || t.endsWith("template3")) return "template3";
  return "template1";
}

function Group({ title, subtitle, defaultOpen = true, children, t = (k, f) => f }) {
  return (
    <details open={defaultOpen} className="rounded-xl border border-gray-200 bg-white">
      <summary className="cursor-pointer select-none list-none px-4 py-3 flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-gray-900">{title}</div>
          {subtitle ? <div className="text-xs text-gray-500 mt-0.5">{subtitle}</div> : null}
        </div>
        <div className="text-xs text-gray-400 mt-0.5">
          {t("serviceEditor.common.toggle", "Toggle")}
        </div>
      </summary>
      <div className="px-4 pb-4 pt-1">{children}</div>
    </details>
  );
}

function Segmented({ value, onChange, options }) {
  return (
    <div className="inline-flex p-1 rounded-xl bg-gray-100 border border-gray-200 flex-wrap">
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${active
              ? "bg-white text-gray-900 shadow-sm ring-1 ring-gray-200"
              : "text-gray-600 hover:text-gray-900"
              }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

const listToText = (arr) => (Array.isArray(arr) ? arr.filter(Boolean).join("\n") : "");
const textToList = (txt) =>
  String(txt || "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);

const nextNumericId = (items) => {
  const maxId = (Array.isArray(items) ? items : []).reduce((m, it) => {
    const n = Number(it?.id);
    return Number.isFinite(n) ? Math.max(m, n) : m;
  }, 0);
  return maxId + 1;
};

const makeServiceTemplate1 = (id) => ({
  id,
  title: "",
  description: "",
  icon: "💡",
  features: [],
  link: "",
  badge: "",
  buttonLink: "",
  buttonIcon: "",
});

const makeServiceTemplate2 = (id) => ({
  id,
  title: "",
  fullDescription: "",
  image: "",
  price: "",
  duration: "",
  link: "",
  features: [],
  buttonLink: "",
  buttonIcon: "",
});

const makeServiceTemplate3 = (id) => ({
  id,
  name: "",
  description: "",
  price: {
    monthly: "",
    yearly: "",
    onetime: "",
  },
  period: "/month",
  features: [],
  limitations: [],
  buttonLink: "",
  buttonIcon: "",
  popular: false,
  highlighted: false,
});

export default function ServiceSectionEditor({
  config,
  template,
  onChange,
  uploadImage,
  t = (k, f) => f,
}) {
  const cfg = config || {};
  const tplKey = useMemo(() => normalizeServiceTemplate(template), [template]);

  const setField = (key, value) => onChange?.(key, value);

  const direction = cfg.direction || "ltr";
  const title = cfg.title || "";
  const subtitle = cfg.subtitle || "";

  const alignment = cfg.alignment || "center";
  const viewAllText = cfg.viewAllText || "";
  const viewAllLink = cfg.viewAllLink || "";

  const additionalInfo = cfg.additionalInfo || "";
  const customQuoteLink = cfg.customQuoteLink || "";

  const featuredServiceText = cfg.featuredServiceText || "";
  const keyBenefitsText = cfg.keyBenefitsText || "";
  const startingAtText = cfg.startingAtText || "";

  const services = Array.isArray(cfg.services) ? cfg.services : [];

  const setServices = (next) => setField("services", next);

  const addService = () => {
    const id = nextNumericId(services);

    let newItem = makeServiceTemplate1(id);
    if (tplKey === "template2") newItem = makeServiceTemplate2(id);
    if (tplKey === "template3") newItem = makeServiceTemplate3(id);

    setServices([...(services || []), newItem]);
  };

  const removeService = (index) => {
    const next = [...services];
    next.splice(index, 1);
    setServices(next);
  };

  const updateService = (index, key, value) => {
    const next = [...services];
    next[index] = { ...(next[index] || {}), [key]: value };
    setServices(next);
  };

  const updateServiceDeep = (index, path, value) => {
    const next = [...services];
    const item = { ...(next[index] || {}) };

    const parts = String(path).split(".");
    let ref = item;
    for (let i = 0; i < parts.length - 1; i++) {
      const p = parts[i];
      ref[p] = ref[p] && typeof ref[p] === "object" ? { ...ref[p] } : {};
      ref = ref[p];
    }
    ref[parts[parts.length - 1]] = value;

    next[index] = item;
    setServices(next);
  };

  const toggleServiceBool = (index, key) => {
    const next = [...services];
    const item = { ...(next[index] || {}) };
    item[key] = !item[key];
    next[index] = item;
    setServices(next);
  };

  return (
    <div className="space-y-4">
      <Group
        t={t}
        title={t("serviceEditor.group.layoutHeader", "Layout & Header")}
        subtitle={t("serviceEditor.group.layoutHeaderHelp", "Direction and section heading")}
        defaultOpen
      >
        <div className="space-y-4">
          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">
              {t("serviceEditor.header.layoutDirection", "Layout Direction")}
            </div>

            <Segmented
              value={direction}
              onChange={(v) => setField("direction", v)}
              options={[
                { value: "ltr", label: t("serviceEditor.common.ltr", "LTR") },
                { value: "rtl", label: t("serviceEditor.common.rtl", "RTL") },
              ]}
            />
          </div>

          <Field
            label={t("serviceEditor.header.fields.title", "Title")}
            value={title}
            onChange={(v) => setField("title", v)}
          />

          <Field
            label={t("serviceEditor.header.fields.subtitle", "Subtitle")}
            type="textarea"
            value={subtitle}
            onChange={(v) => setField("subtitle", v)}
          />
        </div>
      </Group>

      {(tplKey === "template1" || tplKey === "template3") && (
        <Group
          t={t}
          title={t("serviceEditor.group.sectionSettings", "Section Settings")}
          subtitle={t("serviceEditor.group.sectionSettingsHelp", "Template-specific section options")}
          defaultOpen={false}
        >
          <div className="space-y-4">
            {tplKey === "template1" ? (
              <>
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                  {t("serviceEditor.t1.settingsTitle", "Template 1 Settings")}
                </h3>

                <div className="grid md:grid-cols-2 gap-4">
                  <Field
                    label={t("serviceEditor.t1.viewAllText", "View All Text")}
                    value={viewAllText}
                    onChange={(v) => setField("viewAllText", v)}
                    placeholder={t("serviceEditor.t1.viewAllTextPlaceholder", "View All Services")}
                  />
                  <Field
                    label={t("serviceEditor.t1.viewAllLink", "View All Link")}
                    value={viewAllLink}
                    onChange={(v) => setField("viewAllLink", v)}
                    placeholder={t("serviceEditor.t1.viewAllLinkPlaceholder", "/services or https://...")}
                  />
                </div>

                <div>
                  <div className="text-sm font-medium text-gray-700 mb-2">
                    {t("serviceEditor.header.alignment", "Alignment")}
                  </div>

                  <Segmented
                    value={alignment}
                    onChange={(v) => setField("alignment", v)}
                    options={[
                      { value: "left", label: t("serviceEditor.common.left", "Left") },
                      { value: "center", label: t("serviceEditor.common.center", "Center") },
                      { value: "right", label: t("serviceEditor.common.right", "Right") },
                    ]}
                  />
                </div>
              </>
            ) : null}

            {tplKey === "template3" ? (
              <>
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                  {t("serviceEditor.t3.settingsTitle", "Template 3 Settings")}
                </h3>

                <Field
                  label={t("serviceEditor.t3.additionalInfo", "Additional Info")}
                  type="textarea"
                  value={additionalInfo}
                  onChange={(v) => setField("additionalInfo", v)}
                  placeholder={t(
                    "serviceEditor.t3.additionalInfoPlaceholder",
                    "All plans include a 14-day free trial..."
                  )}
                />


              </>
            ) : null}
          </div>
        </Group>
      )}

      <Group
        t={t}
        title={t("serviceEditor.group.services", "Services")}
        subtitle={t("serviceEditor.group.servicesHelp", "Manage the cards or plans shown in this section")}
        defaultOpen
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
              {t("serviceEditor.list.title", "Services")}
            </h3>

            <button
              type="button"
              onClick={addService}
              className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-white"
            >
              {t("serviceEditor.list.addService", "Add Service")}
            </button>
          </div>

          {services.length === 0 ? (
            <div className="text-sm text-gray-600">
              {t("serviceEditor.list.empty", 'No services yet. Click “Add Service”.')}
            </div>
          ) : (
            <div className="space-y-4">
              {services.map((s, i) => (
                <div
                  key={s?.id ?? i}
                  className="p-4 border border-gray-200 rounded-xl bg-gray-50 space-y-4"
                >
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-gray-700">
                      {t("serviceEditor.list.service", "Service")} {i + 1}{" "}
                      {s?.id ? `(${t("serviceEditor.list.id", "ID")}: ${s.id})` : ""}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeService(i)}
                      className="text-red-500 text-sm hover:underline"
                    >
                      {t("serviceEditor.common.remove", "Remove")}
                    </button>
                  </div>

                  {tplKey === "template1" ? (
                    <div className="space-y-4">
                      <Group
                        t={t}
                        title={t("serviceEditor.group.itemContent", "Content")}
                        subtitle={t("serviceEditor.group.itemContentHelp", "Main text, icon, badge, and action link")}
                        defaultOpen
                      >
                        <div className="space-y-4">
                          <div className="grid md:grid-cols-2 gap-4">
                            <Field
                              label={t("serviceEditor.t1.item.title", "Title")}
                              value={s?.title || ""}
                              onChange={(v) => updateService(i, "title", v)}
                            />
                            <Field
                              label={t("serviceEditor.t1.item.icon", "Icon (emoji)")}
                              value={s?.icon || ""}
                              onChange={(v) => updateService(i, "icon", v)}
                              placeholder={t("serviceEditor.t1.item.iconPlaceholder", "💡")}
                            />
                          </div>

                          <Field
                            label={t("serviceEditor.common.description", "Description")}
                            type="textarea"
                            value={s?.description || ""}
                            onChange={(v) => updateService(i, "description", v)}
                          />

                          <div className="grid md:grid-cols-2 gap-4">
                            <Field
                              label={t("serviceEditor.t1.item.badgeOptional", "Badge (optional)")}
                              value={s?.badge || ""}
                              onChange={(v) => updateService(i, "badge", v)}
                              placeholder={t("serviceEditor.t1.item.badgePlaceholder", "Popular")}
                            />
                          </div>
                        </div>
                      </Group>

                      <Group
                        t={t}
                        title={t("serviceEditor.group.itemFeatures", "Features")}
                        subtitle={t("serviceEditor.group.itemFeaturesHelp", "Short bullet points for this service")}
                        defaultOpen={false}
                      >
                        <Field
                          label={t("serviceEditor.common.featuresOnePerLine", "Features (one per line)")}
                          type="textarea"
                          value={listToText(s?.features)}
                          onChange={(v) => updateService(i, "features", textToList(v))}
                          placeholder={t(
                            "serviceEditor.common.featuresPlaceholder",
                            "Feature 1\nFeature 2\nFeature 3"
                          )}
                        />
                      </Group>

                    </div>
                  ) : null}

                  {tplKey === "template2" ? (
                    <div className="space-y-4">
                      <Group
                        t={t}
                        title={t("serviceEditor.t2.labelsTitle", "Template 2 Text Labels")}
                        subtitle={t("serviceEditor.t2.labelsHelp", "Edit fixed texts shown in template 2")}
                        defaultOpen={false}
                      >
                        <div className="space-y-4">
                          <Field
                            label={t("serviceEditor.t2.featuredServiceText", "Featured Service Text")}
                            value={featuredServiceText}
                            onChange={(v) => setField("featuredServiceText", v)}
                            placeholder={t("serviceEditor.t2.featuredServiceTextPlaceholder", "Featured Service")}
                          />

                          <Field
                            label={t("serviceEditor.t2.keyBenefitsText", "Key Benefits Text")}
                            value={keyBenefitsText}
                            onChange={(v) => setField("keyBenefitsText", v)}
                            placeholder={t("serviceEditor.t2.keyBenefitsTextPlaceholder", "Key Benefits")}
                          />

                          <Field
                            label={t("serviceEditor.t2.startingAtText", "Starting At Text")}
                            value={startingAtText}
                            onChange={(v) => setField("startingAtText", v)}
                            placeholder={t("serviceEditor.t2.startingAtTextPlaceholder", "Starting at")}
                          />
                        </div>
                      </Group>

                      <Group
                        t={t}
                        title={t("serviceEditor.group.itemContent", "Content")}
                        subtitle={t(
                          "serviceEditor.group.itemContentHelp",
                          "Main text, pricing, duration, and action link"
                        )}
                        defaultOpen
                      >
                        <div className="space-y-4">
                          <Field
                            label={t("serviceEditor.t2.item.title", "Title")}
                            value={s?.title || ""}
                            onChange={(v) => updateService(i, "title", v)}
                          />

                          <Field
                            label={t("serviceEditor.t2.item.fullDescription", "Full Description")}
                            type="textarea"
                            value={s?.fullDescription || ""}
                            onChange={(v) => updateService(i, "fullDescription", v)}
                          />

                          <div className="grid md:grid-cols-2 gap-4">
                            <Field
                              label={t("serviceEditor.t2.item.price", "Price")}
                              value={s?.price || ""}
                              onChange={(v) => updateService(i, "price", v)}
                              placeholder={t("serviceEditor.t2.item.pricePlaceholder", "From $2,500")}
                            />
                            <Field
                              label={t("serviceEditor.t2.item.duration", "Duration")}
                              value={s?.duration || ""}
                              onChange={(v) => updateService(i, "duration", v)}
                              placeholder={t("serviceEditor.t2.item.durationPlaceholder", "2-4 weeks")}
                            />
                          </div>

                        </div>
                      </Group>

                      <Group
                        t={t}
                        title={t("serviceEditor.group.itemFeatures", "Features")}
                        subtitle={t("serviceEditor.group.itemFeaturesHelp", "Benefits or highlights for this service")}
                        defaultOpen={false}
                      >
                        <Field
                          label={t("serviceEditor.common.featuresOnePerLine", "Features (one per line)")}
                          type="textarea"
                          value={listToText(s?.features)}
                          onChange={(v) => updateService(i, "features", textToList(v))}
                          placeholder={t(
                            "serviceEditor.t2.item.featuresPlaceholder",
                            "Benefit 1\nBenefit 2\nBenefit 3"
                          )}
                        />
                      </Group>

                      <Group
                        t={t}
                        title={t("serviceEditor.group.itemImage", "Image")}
                        subtitle={t("serviceEditor.group.itemImageHelp", "Paste a URL or upload an image")}
                        defaultOpen={false}
                      >
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="text-sm font-medium text-gray-700">
                              {t("serviceEditor.common.image", "Image")}
                            </div>

                            <button
                              type="button"
                              onClick={() => updateService(i, "image", "")}
                              className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-white"
                            >
                              {t("serviceEditor.common.removeImage", "Remove image")}
                            </button>
                          </div>

                          <Field
                            label={t("serviceEditor.t2.item.imageUrl", "Image URL (optional)")}
                            type="url"
                            value={s?.image || ""}
                            onChange={(v) => updateService(i, "image", v)}
                            placeholder={t("serviceEditor.t2.item.imageUrlPlaceholder", "Paste image URL, or upload below")}
                          />

                          {s?.image ? (
                            <img
                              src={s.image}
                              alt=""
                              className="w-full max-h-[240px] object-cover rounded-xl border border-gray-200 bg-white"
                            />
                          ) : (
                            <div className="w-full rounded-xl border border-dashed border-gray-300 bg-white px-4 py-8 text-center text-sm text-gray-500">
                              {t("serviceEditor.common.noImage", "No image selected yet")}
                            </div>
                          )}

                          {uploadImage ? (
                            <HeroImageUploader
                              kind="service"
                              uploadImage={uploadImage}
                              onUploaded={(url) => updateService(i, "image", url)}
                            />
                          ) : (
                            <div className="text-xs text-gray-500">
                              {t("serviceEditor.common.uploadNotAvailable", "Upload not available")}
                            </div>
                          )}
                        </div>
                      </Group>

                    </div>
                  ) : null}

                  {tplKey === "template3" ? (
                    <div className="space-y-4">
                      <Group
                        t={t}
                        title={t("serviceEditor.group.planBasics", "Plan Basics")}
                        subtitle={t("serviceEditor.group.planBasicsHelp", "Plan name, period, and description")}
                        defaultOpen
                      >
                        <div className="space-y-4">
                          <div className="grid md:grid-cols-2 gap-4">
                            <Field
                              label={t("serviceEditor.t3.item.planName", "Plan Name")}
                              value={s?.name || ""}
                              onChange={(v) => updateService(i, "name", v)}
                              placeholder={t("serviceEditor.t3.item.planNamePlaceholder", "Starter")}
                            />
                            <Field
                              label={t("serviceEditor.t3.item.periodLabel", "Period Label")}
                              value={s?.period || ""}
                              onChange={(v) => updateService(i, "period", v)}
                              placeholder={t("serviceEditor.t3.item.periodLabelPlaceholder", "/month")}
                            />
                          </div>

                          <Field
                            label={t("serviceEditor.common.description", "Description")}
                            type="textarea"
                            value={s?.description || ""}
                            onChange={(v) => updateService(i, "description", v)}
                          />
                        </div>
                      </Group>

                      <Group
                        t={t}
                        title={t("serviceEditor.group.pricing", "Pricing")}
                        subtitle={t("serviceEditor.group.pricingHelp", "Monthly, yearly, and one-time values")}
                        defaultOpen={false}
                      >
                        <div className="grid md:grid-cols-3 gap-4">
                          <Field
                            label={t("serviceEditor.t3.item.priceMonthly", "Price Monthly")}
                            value={s?.price?.monthly || ""}
                            onChange={(v) => updateServiceDeep(i, "price.monthly", v)}
                            placeholder={t("serviceEditor.t3.item.priceMonthlyPlaceholder", "$29")}
                          />
                          <Field
                            label={t("serviceEditor.t3.item.priceYearly", "Price Yearly")}
                            value={s?.price?.yearly || ""}
                            onChange={(v) => updateServiceDeep(i, "price.yearly", v)}
                            placeholder={t("serviceEditor.t3.item.priceYearlyPlaceholder", "$290")}
                          />
                          <Field
                            label={t("serviceEditor.t3.item.priceOnetime", "Price One-time")}
                            value={s?.price?.onetime || ""}
                            onChange={(v) => updateServiceDeep(i, "price.onetime", v)}
                            placeholder={t("serviceEditor.t3.item.priceOnetimePlaceholder", "$499")}
                          />
                        </div>
                      </Group>

                      <Group
                        t={t}
                        title={t("serviceEditor.group.featuresAndLimits", "Features & Limitations")}
                        subtitle={t(
                          "serviceEditor.group.featuresAndLimitsHelp",
                          "What is included and what is limited"
                        )}
                        defaultOpen={false}
                      >
                        <div className="space-y-4">
                          <Field
                            label={t("serviceEditor.t3.item.featuresIncluded", "Features Included (one per line)")}
                            type="textarea"
                            value={listToText(s?.features)}
                            onChange={(v) => updateService(i, "features", textToList(v))}
                          />

                          <Field
                            label={t("serviceEditor.t3.item.limitations", "Limitations (one per line)")}
                            type="textarea"
                            value={listToText(s?.limitations)}
                            onChange={(v) => updateService(i, "limitations", textToList(v))}
                          />
                        </div>
                      </Group>

                      <Group
                        t={t}
                        title={t("serviceEditor.group.actionsAndHighlight", "Actions & Highlighting")}
                        subtitle={t(
                          "serviceEditor.group.actionsAndHighlightHelp",
                          "Action link, icon, and plan states"
                        )}
                        defaultOpen={false}
                      >
                        <div className="space-y-4">

                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => toggleServiceBool(i, "popular")}
                              className={`px-3 py-2 rounded-lg border text-sm font-semibold ${s?.popular
                                ? "bg-blue-600 text-white border-blue-600"
                                : "bg-white text-gray-700"
                                }`}
                            >
                              {t("serviceEditor.t3.item.popular", "Popular")}
                            </button>

                            <button
                              type="button"
                              onClick={() => toggleServiceBool(i, "highlighted")}
                              className={`px-3 py-2 rounded-lg border text-sm font-semibold ${s?.highlighted
                                ? "bg-blue-600 text-white border-blue-600"
                                : "bg-white text-gray-700"
                                }`}
                            >
                              {t("serviceEditor.t3.item.highlighted", "Highlighted")}
                            </button>
                          </div>
                        </div>
                      </Group>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      </Group>
    </div>
  );
}